import {
  type PageTemplateIdOf,
  STOREFRONT_REQUIRED_SECTIONS,
  type StorefrontPage,
  type StorefrontPageTemplateIds,
} from "@loot-os/api-contract";

import type { AccountContent } from "./account/accountContent.js";
import { AccountAdvanced } from "./account/sections/advanced/AccountAdvanced.js";
import { AccountHeader } from "./account/sections/header/AccountHeader.js";
import { AccountHistory } from "./account/sections/history/AccountHistory.js";
import { AccountNotices } from "./account/sections/notices/AccountNotices.js";
import { AccountProfile } from "./account/sections/profile/AccountProfile.js";
import { AccountSettingsNav } from "./account/sections/settings-nav/AccountSettingsNav.js";
import { AccountShipping } from "./account/sections/shipping/AccountShipping.js";
import { AccountTabs } from "./account/sections/tabs/AccountTabs.js";
import { AccountVerification } from "./account/sections/verification/AccountVerification.js";
import { AccountShowroomLayout } from "./account/showroom/AccountShowroom.js";
import type {
  BattleCreateContent,
  BattleRoomContent,
  BattlesLobbyContent,
} from "./battles/battlesContent.js";
import { BattleEntry } from "./battles/create/sections/entry/BattleEntry.js";
import { BattleCreateHeading } from "./battles/create/sections/heading/BattleCreateHeading.js";
import { BattlePicks } from "./battles/create/sections/picks/BattlePicks.js";
import { BattleSettings } from "./battles/create/sections/settings/BattleSettings.js";
import { BattleCreateShowroomLayout } from "./battles/create/showroom/BattleCreateShowroom.js";
import { BattlesFilter } from "./battles/lobby/sections/filter/BattlesFilter.js";
import { BattlesHeading } from "./battles/lobby/sections/heading/BattlesHeading.js";
import { BattlesList } from "./battles/lobby/sections/list/BattlesList.js";
import { BattlesShowroomLayout } from "./battles/lobby/showroom/BattlesShowroom.js";
import { BattleFairness } from "./battles/room/sections/fairness/BattleFairness.js";
import { BattleRoomHeader } from "./battles/room/sections/header/BattleRoomHeader.js";
import { BattleOutcome } from "./battles/room/sections/outcome/BattleOutcome.js";
import { BattleRoomResults } from "./battles/room/sections/results/BattleRoomResults.js";
import { BattleRoomSeats } from "./battles/room/sections/seats/BattleRoomSeats.js";
import { BattleRoomStage } from "./battles/room/sections/stage/BattleRoomStage.js";
import { BattleRoomShowroomLayout } from "./battles/room/showroom/BattleRoomShowroom.js";
import { BattleNotice } from "./battles/shared/BattleNotice.js";
import type { BoxPageContent } from "./box/boxPageContent.js";
import { BoxPageBreadcrumb } from "./box/sections/breadcrumb/BoxPageBreadcrumb.js";
import { BoxPageHeading } from "./box/sections/heading/BoxPageHeading.js";
import { BoxPageOpener } from "./box/sections/opener/BoxPageOpener.js";
import { BoxPageSplitLayout, SplitContents } from "./box/split/BoxPageSplit.js";
import {
  BoxPageStackedLayout,
  StackedBreadcrumb,
  StackedContents,
  StackedHeading,
} from "./box/stacked/BoxPageStacked.js";
import type { BoxesContent } from "./boxes/boxesContent.js";
import { BoxesBreadcrumb } from "./boxes/sections/breadcrumb/BoxesBreadcrumb.js";
import { BoxesCategories } from "./boxes/sections/categories/BoxesCategories.js";
import { BoxesFilters } from "./boxes/sections/filters/BoxesFilters.js";
import { BoxesGrid } from "./boxes/sections/grid/BoxesGrid.js";
import { BoxesTabs } from "./boxes/sections/tabs/BoxesTabs.js";
import { BoxesTitle } from "./boxes/sections/title/BoxesTitle.js";
import { BoxesTopHits } from "./boxes/sections/top-hits/BoxesTopHits.js";
import { BoxesShowroomLayout } from "./boxes/showroom/BoxesShowroom.js";
import type { PageTemplate } from "./contract.js";
import type { FairnessContent } from "./fairness/fairnessContent.js";
import { FairnessIntro } from "./fairness/sections/intro/FairnessIntro.js";
import { FairnessSeed } from "./fairness/sections/seed/FairnessSeed.js";
import { FairnessVerify } from "./fairness/sections/verify/FairnessVerify.js";
import { FairnessShowroomLayout } from "./fairness/showroom/FairnessShowroom.js";
import type { ForgeBuilderContent, ForgeContent, ForgeMineContent } from "./forge/forgeContent.js";
import { ForgeBuilderHeading } from "./forge/sections/builder-heading/ForgeBuilderHeading.js";
import { ForgeDetails } from "./forge/sections/details/ForgeDetails.js";
import { ForgeEarnings } from "./forge/sections/earnings/ForgeEarnings.js";
import { ForgeGrid } from "./forge/sections/grid/ForgeGrid.js";
import { ForgeHeading } from "./forge/sections/heading/ForgeHeading.js";
import { ForgeMineBoxes } from "./forge/sections/mine-boxes/ForgeMineBoxes.js";
import { ForgeMineHeading } from "./forge/sections/mine-heading/ForgeMineHeading.js";
import { ForgeNotice } from "./forge/sections/notice/ForgeNotice.js";
import { ForgeItemPicker } from "./forge/sections/picker/ForgeItemPicker.js";
import { ForgeRates } from "./forge/sections/rates/ForgeRates.js";
import { ForgeSortTabs } from "./forge/sections/sort/ForgeSortTabs.js";
import { ForgeSummary } from "./forge/sections/summary/ForgeSummary.js";
import { ForgeBuilderShowroomLayout } from "./forge/showroom/ForgeBuilderShowroom.js";
import { ForgeMineShowroomLayout } from "./forge/showroom/ForgeMineShowroom.js";
import { ForgeShowroomLayout } from "./forge/showroom/ForgeShowroom.js";
import type { HomeContent } from "./home/homeContent.js";
import { HOME_SECTIONS } from "./home/homeSections.js";
import type { InventoryContent } from "./inventory/inventoryContent.js";
import { InventorySelectionBar } from "./inventory/sections/selection/InventorySelectionBar.js";
import { InventoryVault } from "./inventory/sections/vault/InventoryVault.js";
import { InventoryShowroomLayout } from "./inventory/showroom/InventoryShowroom.js";
import type { LoginContent } from "./login/loginContent.js";
import { LoginPanel } from "./login/sections/panel/LoginPanel.js";
import { LoginShowroomLayout } from "./login/showroom/LoginShowroom.js";
import { type SectionList, sectionListTemplate } from "./sections.js";

/**
 * The page templates: each a page's layout plus one section component per key of its content.
 * Every template of a page takes the same content, so a store switches layout by id without
 * touching its data. Render one with PageTemplateView inside the app's StoreChrome.
 */

/**
 * Each Home template as a list of its sections: which it places, in what order and slot, and the
 * variant and settings each is drawn in (its defaults where left out). The shape a store's own
 * Home will be stored in (api-contract's `PageSectionList`); the lab starts from these.
 */
export const HOME_LISTS = {
  /** Catalog first: every section, each in its default variant. */
  showroom: {
    layout: "column",
    spacing: "roomy",
    slots: {
      lead: [{ section: "hero" }],
      body: [
        { section: "liveHits" },
        { section: "recentOpens" },
        { section: "featuredBoxes" },
        { section: "battles" },
        { section: "forge" },
        { section: "promoCards" },
        { section: "products" },
        { section: "reviews" },
        { section: "promises" },
        { section: "faq" },
        { section: "closing" },
      ],
    },
  },
  /** A few boxes up close: the boxes large, the promises straight after, the hero and FAQ centered. */
  spotlight: {
    layout: "column",
    spacing: "roomy",
    slots: {
      lead: [{ section: "hero", variant: "centered" }],
      body: [
        { section: "liveHits" },
        { section: "featuredBoxes", variant: "large" },
        { section: "promises" },
        { section: "recentOpens" },
        { section: "battles" },
        { section: "forge" },
        { section: "promoCards" },
        { section: "reviews" },
        { section: "products" },
        { section: "faq", variant: "centered" },
        { section: "closing" },
      ],
    },
  },
  /**
   * A busy floor of rails, packed closer. Rails and banners lead it, so it leaves out the promo
   * cards (the header's nav already links the games and fairness) and the closing band.
   */
  arcade: {
    layout: "column",
    spacing: "tight",
    slots: {
      lead: [{ section: "hero", variant: "carousel", settings: { shine: true } }],
      body: [
        { section: "featuredBoxes", variant: "rail" },
        { section: "liveHits", variant: "cards" },
        { section: "recentOpens", variant: "cards" },
        { section: "battles", variant: "rows" },
        { section: "forge", variant: "stage" },
        { section: "products" },
        { section: "reviews" },
        { section: "promises" },
        { section: "faq", variant: "stacked" },
      ],
    },
  },
} as const satisfies Record<string, SectionList<typeof HOME_SECTIONS>>;

/** A Home template from its list, held to the sections Home requires. */
function homeTemplate(list: SectionList<typeof HOME_SECTIONS>) {
  return sectionListTemplate<HomeContent, typeof HOME_SECTIONS>(
    HOME_SECTIONS,
    list,
    STOREFRONT_REQUIRED_SECTIONS.home ?? [],
  );
}

/** Home as a catalog-first store with many boxes. */
export const HOME_SHOWROOM: PageTemplate<HomeContent> = homeTemplate(HOME_LISTS.showroom);

/** Home for a small catalog or a creator's picks: the few boxes large, the hero and FAQ centered. */
export const HOME_SPOTLIGHT: PageTemplate<HomeContent> = homeTemplate(HOME_LISTS.spotlight);

/** Home as a busy floor: rails of boxes and pulls, live battles, the Forge on a stage. */
export const HOME_ARCADE: PageTemplate<HomeContent> = homeTemplate(HOME_LISTS.arcade);

/** The boxes catalog for a showroom store. */
export const BOXES_SHOWROOM: PageTemplate<BoxesContent> = {
  Layout: BoxesShowroomLayout,
  sections: {
    breadcrumb: BoxesBreadcrumb,
    title: BoxesTitle,
    tabs: BoxesTabs,
    categories: BoxesCategories,
    filters: BoxesFilters,
    topHits: BoxesTopHits,
    grid: BoxesGrid,
  },
};

/** The battles lobby for a showroom store. */
export const BATTLES_SHOWROOM: PageTemplate<BattlesLobbyContent> = {
  Layout: BattlesShowroomLayout,
  sections: { heading: BattlesHeading, filter: BattlesFilter, list: BattlesList },
};

/** Building a battle, for a showroom store. */
export const BATTLE_CREATE_SHOWROOM: PageTemplate<BattleCreateContent> = {
  Layout: BattleCreateShowroomLayout,
  sections: {
    heading: BattleCreateHeading,
    notice: BattleNotice,
    picks: BattlePicks,
    settings: BattleSettings,
    entry: BattleEntry,
  },
};

/** One battle's room, for a showroom store. */
export const BATTLE_ROOM_SHOWROOM: PageTemplate<BattleRoomContent> = {
  Layout: BattleRoomShowroomLayout,
  sections: {
    header: BattleRoomHeader,
    notice: BattleNotice,
    seats: BattleRoomSeats,
    stage: BattleRoomStage,
    outcome: BattleOutcome,
    results: BattleRoomResults,
    fairness: BattleFairness,
  },
};

/** The shopper's Account page (settings, history) for a showroom store. */
export const ACCOUNT_SHOWROOM: PageTemplate<AccountContent> = {
  Layout: AccountShowroomLayout,
  sections: {
    header: AccountHeader,
    tabs: AccountTabs,
    notices: AccountNotices,
    settingsNav: AccountSettingsNav,
    profile: AccountProfile,
    shipping: AccountShipping,
    verification: AccountVerification,
    advanced: AccountAdvanced,
    history: AccountHistory,
  },
};

/** The shopper's inventory (the account's Vault tab) for a showroom store. */
export const INVENTORY_SHOWROOM: PageTemplate<InventoryContent> = {
  Layout: InventoryShowroomLayout,
  sections: {
    header: AccountHeader,
    tabs: AccountTabs,
    vault: InventoryVault,
    selection: InventorySelectionBar,
  },
};

/** A box's page split: the box and its opener beside the contents. */
export const BOX_PAGE_SPLIT: PageTemplate<BoxPageContent> = {
  Layout: BoxPageSplitLayout,
  sections: {
    breadcrumb: BoxPageBreadcrumb,
    heading: BoxPageHeading,
    opener: BoxPageOpener,
    contents: SplitContents,
  },
};

/** A box's page stacked: the box and its opener on a full-bleed stage, the contents below. */
export const BOX_PAGE_STACKED: PageTemplate<BoxPageContent> = {
  Layout: BoxPageStackedLayout,
  sections: {
    breadcrumb: StackedBreadcrumb,
    heading: StackedHeading,
    opener: BoxPageOpener,
    contents: StackedContents,
  },
};

/** The forge's browse page for a showroom store. Composed without a design. */
export const FORGE_SHOWROOM: PageTemplate<ForgeContent> = {
  Layout: ForgeShowroomLayout,
  sections: { heading: ForgeHeading, sort: ForgeSortTabs, grid: ForgeGrid },
};

/** The forge builder (create and edit) for a showroom store. Composed without a design. */
export const FORGE_BUILDER_SHOWROOM: PageTemplate<ForgeBuilderContent> = {
  Layout: ForgeBuilderShowroomLayout,
  sections: {
    heading: ForgeBuilderHeading,
    notice: ForgeNotice,
    details: ForgeDetails,
    picker: ForgeItemPicker,
    rates: ForgeRates,
    summary: ForgeSummary,
  },
};

/** A creator's own forge boxes for a showroom store. Composed without a design. */
export const FORGE_MINE_SHOWROOM: PageTemplate<ForgeMineContent> = {
  Layout: ForgeMineShowroomLayout,
  sections: { heading: ForgeMineHeading, earnings: ForgeEarnings, boxes: ForgeMineBoxes },
};

/** The sign-in page for a showroom store. */
export const LOGIN_SHOWROOM: PageTemplate<LoginContent> = {
  Layout: LoginShowroomLayout,
  sections: { panel: LoginPanel },
};

/** The fairness page (the verifier and the seed lookup) for a showroom store. */
export const FAIRNESS_SHOWROOM: PageTemplate<FairnessContent> = {
  Layout: FairnessShowroomLayout,
  sections: { intro: FairnessIntro, verify: FairnessVerify, seed: FairnessSeed },
};

/** The templates by page and id, as a store picks them. */
export const HOME_TEMPLATES = {
  showroom: { label: "Showroom", template: HOME_SHOWROOM },
  spotlight: { label: "Spotlight", template: HOME_SPOTLIGHT },
  arcade: { label: "Arcade", template: HOME_ARCADE },
} as const satisfies Record<string, { label: string; template: PageTemplate<HomeContent> }>;

export const BOXES_TEMPLATES = {
  showroom: { label: "Showroom", template: BOXES_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<BoxesContent> }>;

export const BATTLES_TEMPLATES = {
  showroom: { label: "Showroom", template: BATTLES_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<BattlesLobbyContent> }>;

export const BATTLE_CREATE_TEMPLATES = {
  showroom: { label: "Showroom", template: BATTLE_CREATE_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<BattleCreateContent> }>;

export const BATTLE_ROOM_TEMPLATES = {
  showroom: { label: "Showroom", template: BATTLE_ROOM_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<BattleRoomContent> }>;

export const ACCOUNT_TEMPLATES = {
  showroom: { label: "Showroom", template: ACCOUNT_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<AccountContent> }>;

export const INVENTORY_TEMPLATES = {
  showroom: { label: "Showroom", template: INVENTORY_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<InventoryContent> }>;

export const BOX_PAGE_TEMPLATES = {
  split: { label: "Split", template: BOX_PAGE_SPLIT },
  stacked: { label: "Stacked", template: BOX_PAGE_STACKED },
} as const satisfies Record<string, { label: string; template: PageTemplate<BoxPageContent> }>;

export const FORGE_TEMPLATES = {
  showroom: { label: "Showroom", template: FORGE_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<ForgeContent> }>;

export const FORGE_BUILDER_TEMPLATES = {
  showroom: { label: "Showroom", template: FORGE_BUILDER_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<ForgeBuilderContent> }>;

export const FORGE_MINE_TEMPLATES = {
  showroom: { label: "Showroom", template: FORGE_MINE_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<ForgeMineContent> }>;

export const LOGIN_TEMPLATES = {
  showroom: { label: "Showroom", template: LOGIN_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<LoginContent> }>;

export const FAIRNESS_TEMPLATES = {
  showroom: { label: "Showroom", template: FAIRNESS_SHOWROOM },
} as const satisfies Record<string, { label: string; template: PageTemplate<FairnessContent> }>;

export type HomeTemplateId = keyof typeof HOME_TEMPLATES;
export type BoxesTemplateId = keyof typeof BOXES_TEMPLATES;
export type BoxPageTemplateId = keyof typeof BOX_PAGE_TEMPLATES;
export type LoginTemplateId = keyof typeof LOGIN_TEMPLATES;
export type FairnessTemplateId = keyof typeof FAIRNESS_TEMPLATES;
export type BattlesTemplateId = keyof typeof BATTLES_TEMPLATES;
export type BattleCreateTemplateId = keyof typeof BATTLE_CREATE_TEMPLATES;
export type BattleRoomTemplateId = keyof typeof BATTLE_ROOM_TEMPLATES;
export type AccountTemplateId = keyof typeof ACCOUNT_TEMPLATES;
export type InventoryTemplateId = keyof typeof INVENTORY_TEMPLATES;

/** Every page's catalog, by the page keys of `STOREFRONT_PAGE_TEMPLATE_IDS` in api-contract. */
export const PAGE_TEMPLATE_CATALOGS = {
  home: HOME_TEMPLATES,
  boxes: BOXES_TEMPLATES,
  battles: BATTLES_TEMPLATES,
  battleCreate: BATTLE_CREATE_TEMPLATES,
  battle: BATTLE_ROOM_TEMPLATES,
  account: ACCOUNT_TEMPLATES,
  inventory: INVENTORY_TEMPLATES,
  box: BOX_PAGE_TEMPLATES,
  forge: FORGE_TEMPLATES,
  forgeBuilder: FORGE_BUILDER_TEMPLATES,
  forgeMine: FORGE_MINE_TEMPLATES,
  login: LOGIN_TEMPLATES,
  fairness: FAIRNESS_TEMPLATES,
} as const satisfies {
  [P in StorefrontPage]: Record<PageTemplateIdOf<P>, { label: string; template: unknown }>;
};

/** One page template for every storefront page, as an app renders them. */
export interface StorefrontPageTemplates {
  home: PageTemplate<HomeContent>;
  boxes: PageTemplate<BoxesContent>;
  /** The battles lobby. */
  battles: PageTemplate<BattlesLobbyContent>;
  /** Building a battle. */
  battleCreate: PageTemplate<BattleCreateContent>;
  /** One battle's room. */
  battle: PageTemplate<BattleRoomContent>;
  account: PageTemplate<AccountContent>;
  inventory: PageTemplate<InventoryContent>;
  /** A box's page. */
  box: PageTemplate<BoxPageContent>;
  /** The forge's browse page. */
  forge: PageTemplate<ForgeContent>;
  /** The forge builder, creating and editing. */
  forgeBuilder: PageTemplate<ForgeBuilderContent>;
  /** The creator's own forge boxes and earnings. */
  forgeMine: PageTemplate<ForgeMineContent>;
  login: PageTemplate<LoginContent>;
  fairness: PageTemplate<FairnessContent>;
}

/**
 * The page templates for a page template id per page (`resolveStorefrontPageTemplates` in
 * api-contract decides the ids from a store's choices; this only looks them up).
 */
export function pageTemplatesFor(ids: StorefrontPageTemplateIds): StorefrontPageTemplates {
  const c = PAGE_TEMPLATE_CATALOGS;
  return {
    home: c.home[ids.home].template,
    boxes: c.boxes[ids.boxes].template,
    battles: c.battles[ids.battles].template,
    battleCreate: c.battleCreate[ids.battleCreate].template,
    battle: c.battle[ids.battle].template,
    account: c.account[ids.account].template,
    inventory: c.inventory[ids.inventory].template,
    box: c.box[ids.box].template,
    forge: c.forge[ids.forge].template,
    forgeBuilder: c.forgeBuilder[ids.forgeBuilder].template,
    forgeMine: c.forgeMine[ids.forgeMine].template,
    login: c.login[ids.login].template,
    fairness: c.fairness[ids.fairness].template,
  };
}
